import React, { useCallback, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faHandHoldingUsd, faToggleOn, faToggleOff, faInfoCircle, faGift, faCoins, faExclamationTriangle,
    faDownload, faUserTie, faUserCheck, faLock, faSort, faSortAmountDown, faCheck, faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { ref, remove, update } from 'firebase/database';
import { database } from '../../firebase/firebase';
import { useAuth } from '../../hooks/useAuth';
import {
    getSettlementsByStrategy, getSponsorCoverageInfo, getSponsorFunds, isSponsorFundId
} from '../../utils/helpers';
import { settlementKeyOf, useSettlementPlan } from '../../hooks/useSettlementPlan';
import { exportSettlementsToCSV } from '../../utils/exportHelpers';
import { useEventUsersCache } from '../../hooks/useEventUsersCache';
import EmptyState from './EmptyState';
import SettlementCard from './SettlementCard';
import ReceiverSummary from './ReceiverSummary';
import { useTranslation } from 'react-i18next';
import {
    Expense, SettlementConfig, SettlementStatusItem, SettlementStrategy, ShareRatios, SponsorExpense
} from '../../types';
import { useFormatCurrency } from '../../hooks/useFormatCurrency';

interface SuggestedPaymentsTabProps {
    expenses: (Expense | SponsorExpense)[];
    participants: Record<string, string>;
    eventId: string;
    eventName: string;
    selectedParticipant?: string;
    isClosed: boolean;
    settlementStatus: Record<string, SettlementStatusItem>;
    settlementConfig?: SettlementConfig;
    shareRatios?: ShareRatios;
    isOwner: boolean;
    // Marking the first transfer closes the event, so the tab needs to be able to
    // close it: nobody remembers to do it by hand before settling up.
    onCloseEvent: () => Promise<void>;
}

const STRATEGY_ICONS = {
    singleCollector: faUserTie,
    simplePay: faUserCheck,
};

const STRATEGIES: SettlementStrategy[] = ['simplePay', 'singleCollector'];

// Below this the sort bar costs more attention than the ordering it buys.
const MIN_SETTLEMENTS_FOR_SORTING = 3;

type SettlementSortMode =
    'default' | 'amountHighToLow' | 'amountLowToHigh' | 'byFromName' | 'byToName' | 'unpaidFirst';

const SuggestedPaymentsTab: React.FC<SuggestedPaymentsTabProps> = ({
                                                                       expenses,
                                                                       participants,
                                                                       eventId,
                                                                       eventName,
                                                                       selectedParticipant,
                                                                       isClosed,
                                                                       settlementStatus = {},
                                                                       settlementConfig,
                                                                       shareRatios,
                                                                       isOwner,
                                                                       onCloseEvent
                                                                   }) => {
    const { user } = useAuth();
    const [updatingPayment, setUpdatingPayment] = useState<string | null>(null);
    const [sortMode, setSortMode] = useState<SettlementSortMode>('default');
    const { t } = useTranslation();
    const { formatCurrency } = useFormatCurrency();

    const { eventUsers, loading: loadingNames } = useEventUsersCache(eventId);

    const participantIds = useMemo(() => Object.keys(participants), [participants]);

    // Shared with the expense summary, which shows how far this plan has got.
    // Once someone has confirmed a transfer the plan must not change underneath
    // them, which is what isPlanLocked guards.
    const {
        strategy,
        collectorId,
        distributeSponsorSurplus,
        balances,
        settlements: rawSettlements,
        isPlanLocked,
    } = useSettlementPlan(expenses, participants, settlementConfig, settlementStatus, shareRatios);

    // External sponsorships nobody holds get a pseudo-participant each, so the plan
    // can show who their cash has to be handed to.
    const sponsorFunds = useMemo(() => getSponsorFunds(expenses), [expenses]);

    const sponsorFundNames = useMemo(() => {
        const names: Record<string, string> = {};
        sponsorFunds.forEach(fund => { names[fund.id] = fund.name; });
        return names;
    }, [sponsorFunds]);

    const nameOf = useCallback(
        (id: string) => participants[id] || sponsorFundNames[id] || id,
        [participants, sponsorFundNames]
    );

    // Every party on the sponsor side of the plan: participants holding sponsor
    // cash, plus the unheld funds. Gating on holders alone hid the sponsor sections
    // — and the coverage summary — from every event whose sponsors had no holder.
    const sponsorPartyIds = useMemo(() => {
        const ids = new Set<string>(sponsorFunds.map(fund => fund.id));
        expenses.forEach(expense => {
            if (expense.type === 'sponsor' && expense.sponsorHolder) {
                ids.add(expense.sponsorHolder);
            }
        });
        return Array.from(ids);
    }, [expenses, sponsorFunds]);

    const hasSponsorParty = sponsorPartyIds.length > 0;

    const sponsorCoverageInfo = useMemo(() => {
        if (!hasSponsorParty) return null;
        return getSponsorCoverageInfo(expenses);
    }, [expenses, hasSponsorParty]);

    // The plan dressed for display: names resolved, sponsor parties flagged.
    const settlements = useMemo(() => (
        rawSettlements.map(settlement => ({
            ...settlement,
            fromName: nameOf(settlement.from),
            toName: nameOf(settlement.to),
            isSponsorRelated: sponsorPartyIds.includes(settlement.from) || sponsorPartyIds.includes(settlement.to),
            isFromSponsorHolder: sponsorPartyIds.includes(settlement.from),
            isToSponsorHolder: sponsorPartyIds.includes(settlement.to),
            // A fund has no expense history of its own, so its card must not try to
            // explain the amount as "shares this person joined".
            isFromSponsorFund: isSponsorFundId(settlement.from)
        }))
    ), [rawSettlements, nameOf, sponsorPartyIds]);

    const sortedSettlements = useMemo(() => {
        const list = [...settlements];
        switch (sortMode) {
            case 'amountHighToLow':
                return list.sort((a, b) => b.amount - a.amount);
            case 'amountLowToHigh':
                return list.sort((a, b) => a.amount - b.amount);
            case 'byFromName':
                return list.sort((a, b) => a.fromName.localeCompare(b.fromName));
            case 'byToName':
                return list.sort((a, b) => a.toName.localeCompare(b.toName));
            case 'unpaidFirst':
                return list.sort((a, b) => {
                    const paidA = settlementStatus[settlementKeyOf(a.from, a.to)]?.paid ? 1 : 0;
                    const paidB = settlementStatus[settlementKeyOf(b.from, b.to)]?.paid ? 1 : 0;
                    return paidA - paidB;
                });
            default:
                return list;
        }
    }, [settlements, sortMode, settlementStatus]);

    // The owner, viewing the event as themselves, is the one who may settle it up.
    const isOwnerActing = isOwner && selectedParticipant === user?.uid;

    const canToggleSettlement = useCallback((from: string, to: string): boolean => {
        if (isOwnerActing) {
            return true;
        }
        // While the event is still open, only the owner marks transfers: the first
        // mark closes the event, and that is the owner's call to make.
        if (!isClosed) {
            return false;
        }
        // A sponsor fund is nobody's seat, so no one could ever mark its transfer.
        // The person receiving the cash is the one who knows it arrived.
        if (isSponsorFundId(from)) {
            return to === selectedParticipant;
        }
        return from === selectedParticipant;
    }, [isClosed, isOwnerActing, selectedParticipant]);

    const toggleSettlementStatus = useCallback(async (
        settlement: { from: string; to: string; fromName: string; toName: string },
        amount: number
    ) => {
        const settlementKey = settlementKeyOf(settlement.from, settlement.to);

        if (!canToggleSettlement(settlement.from, settlement.to)) {
            alert(t('event.payments.errors.onlySelected'));
            return;
        }

        const currentStatus = settlementStatus[settlementKey]?.paid || false;
        const markingPaid = !currentStatus;

        // Money starting to move means the numbers are final, so the event is closed
        // along with the first mark instead of waiting for someone to close it.
        const shouldCloseEvent = markingPaid && !isClosed;
        if (shouldCloseEvent && !window.confirm(t('event.payments.autoClose.confirm'))) {
            return;
        }

        setUpdatingPayment(settlementKey);

        try {
            const settlementRef = ref(database, `events/${eventId}/settlementStatus/${settlementKey}`);

            await update(settlementRef, {
                paid: markingPaid,
                amount: amount,
                from: settlement.from,
                to: settlement.to,
            });
        } catch (error) {
            console.error('Error updating settlement status:', error);
            alert(t('event.payments.errors.updateFailed'));
            setUpdatingPayment(null);
            return;
        }

        // Closed after the mark landed: a failure here leaves the mark in place and
        // the event open, which the owner can still fix by hand.
        try {
            if (shouldCloseEvent) {
                await onCloseEvent();
            }
        } catch (error) {
            console.error('Error closing event after payment mark:', error);
            alert(t('event.payments.autoClose.failed'));
        } finally {
            setUpdatingPayment(null);
        }
    }, [canToggleSettlement, eventId, isClosed, onCloseEvent, settlementStatus, t]);

    // Persist the shared plan. Any leftover (unpaid) marks belong to the previous
    // set of from/to pairs, so they are dropped along with the change.
    const saveConfig = useCallback(async (updates: Partial<SettlementConfig>) => {
        try {
            await update(ref(database, `events/${eventId}/settlementConfig`), {
                strategy,
                collectorId,
                distributeSponsorSurplus,
                ...updates,
                updatedAt: Date.now(),
            });
            await remove(ref(database, `events/${eventId}/settlementStatus`));
        } catch (error) {
            console.error('Error updating settlement config:', error);
            alert(t('event.payments.errors.updateFailed'));
        }
    }, [collectorId, distributeSponsorSurplus, eventId, strategy, t]);

    // There are only two methods, so a locked plan has exactly one place to go.
    const alternativeStrategy = STRATEGIES.find(s => s !== strategy) ?? STRATEGIES[0];

    // Naming the destination is the whole point: "change the method" hides what you
    // would get and nobody dares press it. saveConfig clears the marks on its way.
    const handleSwitchStrategy = useCallback(async () => {
        const name = t(`event.payments.strategy.${alternativeStrategy}.name`);
        if (!window.confirm(t('event.payments.strategy.locked.confirmChange', { name }))) return;
        await saveConfig({ strategy: alternativeStrategy });
    }, [alternativeStrategy, saveConfig, t]);

    // The *first* mark is supposed to close the event, but that write can fail on
    // its own (a dropped connection, a rejected write) and nothing ever retried it.
    // So one mark on an open event is already the broken state — waiting for every
    // row to be ticked would miss it exactly where it is most likely to happen,
    // since the failure is on the mark that had no company yet. The banner below is
    // the way out of it.
    const isSettledButOpen = useMemo(() => (
        !isClosed &&
        settlements.some(s => settlementStatus[settlementKeyOf(s.from, s.to)]?.paid)
    ), [isClosed, settlements, settlementStatus]);

    const [closingStuckEvent, setClosingStuckEvent] = useState(false);

    const handleCloseStuckEvent = useCallback(async () => {
        setClosingStuckEvent(true);
        try {
            await onCloseEvent();
        } catch (error) {
            console.error('Error closing settled event:', error);
            alert(t('event.payments.autoClose.failed'));
        } finally {
            setClosingStuckEvent(false);
        }
    }, [onCloseEvent, t]);

    const handleExportSettlements = () => {
        exportSettlementsToCSV(sortedSettlements, { ...participants, ...sponsorFundNames }, eventName, t);
    };

    if (loadingNames) {
        return (
            <div className="glass-card p-8 flex items-center justify-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin" aria-hidden="true" />
                {t('event.payments.loading')}
            </div>
        );
    }

    const renderStrategySelector = () => (
        <div>
            {/* Both options stay visible so nobody misses the alternative */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {STRATEGIES.map((s) => {
                    const isActive = strategy === s;
                    const count = s === strategy
                        ? settlements.length
                        : getSettlementsByStrategy(balances, s, collectorId).length;

                    return (
                        <button
                            key={s}
                            type="button"
                            onClick={() => !isActive && saveConfig({ strategy: s })}
                            aria-pressed={isActive}
                            title={t(`event.payments.strategy.${s}.description`)}
                            className={`flex items-start gap-2 px-3 py-2.5 rounded-xl text-left transition-all duration-200 border-2 focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                                isActive
                                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 shadow-sm'
                                    : 'border-transparent glass-sub-panel hover:shadow-md'
                            }`}
                        >
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                isActive
                                    ? 'bg-purple-100 dark:bg-purple-900/40'
                                    : 'bg-gray-100 dark:bg-gray-700'
                            }`}>
                                <FontAwesomeIcon
                                    icon={isActive ? faCheck : STRATEGY_ICONS[s]}
                                    className={`text-sm ${
                                        isActive
                                            ? 'text-purple-600 dark:text-purple-400'
                                            : 'text-gray-500 dark:text-gray-400'
                                    }`}
                                    aria-hidden="true"
                                />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                    <span className={`text-sm font-semibold truncate ${
                                        isActive ? 'text-purple-700 dark:text-purple-300' : 'text-gray-900 dark:text-white'
                                    }`}>
                                        {t(`event.payments.strategy.${s}.name`)}
                                    </span>
                                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${
                                        isActive
                                            ? 'bg-purple-200 dark:bg-purple-800 text-purple-700 dark:text-purple-300'
                                            : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                                    }`}>
                                        {t('event.payments.strategy.transactions', { count })}
                                    </span>
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* Single Collector: choose who */}
            {strategy === 'singleCollector' && (
                <div className="mt-2 flex items-center gap-2">
                    <label htmlFor="settlement-collector" className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {t('event.payments.strategy.singleCollector.selectCollector')}:
                    </label>
                    <select
                        id="settlement-collector"
                        value={collectorId}
                        onChange={(e) => saveConfig({ collectorId: e.target.value })}
                        className="flex-1 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                        {participantIds.map(id => (
                            <option key={id} value={id}>
                                {participants[id] || id}
                            </option>
                        ))}
                    </select>
                </div>
            )}
        </div>
    );

    // A locked plan cannot change, so two disabled cards and a paragraph saying why
    // are just noise: it collapses to the method name, and the confirm dialog behind
    // "change" is where the cost of changing it belongs.
    const renderLockedStrategy = () => (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <FontAwesomeIcon
                icon={faLock}
                className="text-xs text-amber-600 dark:text-amber-400 flex-shrink-0"
                aria-hidden="true"
            />
            <span
                className="text-sm font-semibold text-gray-900 dark:text-white min-w-0 truncate"
                title={t('event.payments.strategy.locked.message')}
            >
                {t(`event.payments.strategy.${strategy}.name`)}
                <span className="font-normal text-gray-500 dark:text-gray-400">
                    {strategy === 'singleCollector' && collectorId && ` · ${participants[collectorId] || collectorId}`}
                    {' · '}{t('event.payments.strategy.transactions', { count: settlements.length })}
                </span>
            </span>
            {isOwner && (
                <button
                    type="button"
                    onClick={handleSwitchStrategy}
                    className="ml-auto flex items-center gap-1.5 flex-shrink-0 text-xs font-medium px-2.5 py-1 rounded-lg border border-purple-200 dark:border-purple-800 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                    <FontAwesomeIcon icon={STRATEGY_ICONS[alternativeStrategy]} aria-hidden="true"/>
                    {t('event.payments.strategy.locked.switchTo', {
                        name: t(`event.payments.strategy.${alternativeStrategy}.name`)
                    })}
                </button>
            )}
        </div>
    );

    const sortOptions: { key: SettlementSortMode; label: string }[] = [
        { key: 'default', label: t('event.payments.sorting.default') },
        { key: 'amountHighToLow', label: t('event.payments.sorting.amountHighToLow') },
        { key: 'amountLowToHigh', label: t('event.payments.sorting.amountLowToHigh') },
        { key: 'byFromName', label: t('event.payments.sorting.byFromName') },
        { key: 'byToName', label: t('event.payments.sorting.byToName') },
        { key: 'unpaidFirst', label: t('event.payments.sorting.unpaidFirst') },
    ];

    const renderSortBar = () => (
        <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide">
            <FontAwesomeIcon
                icon={sortMode !== 'default' ? faSortAmountDown : faSort}
                className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0"
                aria-hidden="true"
            />
            {sortOptions.map(opt => (
                <button
                    key={opt.key}
                    type="button"
                    onClick={() => setSortMode(opt.key)}
                    aria-pressed={sortMode === opt.key}
                    className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap transition-all duration-200 ${
                        sortMode === opt.key
                            ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 font-semibold shadow-sm'
                            : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                    }`}
                >
                    {opt.label}
                </button>
            ))}
        </div>
    );

    const renderCard = (settlement: typeof sortedSettlements[number]) => {
        const settlementKey = settlementKeyOf(settlement.from, settlement.to);
        return (
            <SettlementCard
                key={settlementKey}
                settlement={settlement}
                isSettlementPaid={settlementStatus[settlementKey]?.paid || false}
                canToggle={canToggleSettlement(settlement.from, settlement.to)}
                isFromSponsorFund={settlement.isFromSponsorFund}
                isUpdating={updatingPayment === settlementKey}
                expenses={expenses}
                shareRatios={shareRatios}
                recipientProfile={eventUsers[settlement.to] || null}
                eventName={eventName}
                onToggleSettlement={toggleSettlementStatus}
            />
        );
    };

    const renderSettlementsList = () => {
        if (!hasSponsorParty) {
            // No sponsor - simple settlement list
            return (
                <div className="grid gap-2">
                    {sortedSettlements.map(renderCard)}
                </div>
            );
        }

        // Has sponsor - organize by settlement type
        // Unheld external funds handing their cash over is a different thing from a
        // participant holder distributing surplus, so it gets its own section.
        const sponsorFundTransfers = sortedSettlements.filter(s => s.isFromSponsorFund);
        const sponsorToOthers = sortedSettlements.filter(s => s.isFromSponsorHolder && !s.isFromSponsorFund);
        const othersToSponsor = sortedSettlements.filter(s => s.isToSponsorHolder);
        const regularSettlements = sortedSettlements.filter(s => !s.isSponsorRelated);

        return (
            <div className="space-y-4">
                {/* Sponsor money still to be handed over */}
                {sponsorFundTransfers.length > 0 && (
                    <div>
                        <div className="flex items-center mb-2 px-2" title={t('event.payments.sponsorFundSection.description')}>
                            <FontAwesomeIcon icon={faGift} className="mr-2 text-green-600 dark:text-green-400" />
                            <h5 className="text-sm font-semibold text-gray-900 dark:text-white">
                                {t('event.payments.sponsorFundSection.title')}
                            </h5>
                        </div>
                        <div className="grid gap-2">
                            {sponsorFundTransfers.map(renderCard)}
                        </div>
                    </div>
                )}

                {/* Sponsor pays others */}
                {sponsorToOthers.length > 0 && (
                    <div>
                        <div
                            className="flex items-center mb-2 px-2"
                            title={distributeSponsorSurplus ? t('event.payments.sponsor.distribution.description') : t('event.payments.sponsor.reimbursement.description')}
                        >
                            <FontAwesomeIcon icon={faGift} className="mr-2 text-green-600 dark:text-green-400" />
                            <h5 className="text-sm font-semibold text-gray-900 dark:text-white">
                                {distributeSponsorSurplus ? t('event.payments.sponsor.distribution.title') : t('event.payments.sponsor.reimbursement.title')}
                            </h5>
                        </div>
                        <div className="grid gap-2">
                            {sponsorToOthers.map(renderCard)}
                        </div>
                    </div>
                )}

                {/* Others pay sponsor */}
                {othersToSponsor.length > 0 && (
                    <div>
                        <div className="flex items-center mb-2 px-2" title={t('event.payments.member.contributions.description')}>
                            <FontAwesomeIcon icon={faCoins} className="mr-2 text-blue-600 dark:text-blue-400" />
                            <h5 className="text-sm font-semibold text-gray-900 dark:text-white">{t('event.payments.member.contributions.title')}</h5>
                        </div>
                        <div className="grid gap-2">
                            {othersToSponsor.map(renderCard)}
                        </div>
                    </div>
                )}

                {/* Regular settlements (member to member) */}
                {regularSettlements.length > 0 && (
                    <div>
                        <div className="flex items-center mb-2 px-2" title={t('event.payments.member.settlements.description')}>
                            <FontAwesomeIcon icon={faCoins} className="mr-2 text-gray-600 dark:text-gray-400" />
                            <h5 className="text-sm font-semibold text-gray-900 dark:text-white">{t('event.payments.member.settlements.title')}</h5>
                        </div>
                        <div className="grid gap-2">
                            {regularSettlements.map(renderCard)}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (expenses.length === 0) {
        return <EmptyState />;
    }

    if (settlements.length === 0) {
        return (
            <div className="text-center py-12">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FontAwesomeIcon icon={faHandHoldingUsd} className="text-2xl text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {t('event.payments.allSettled.title')}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                    {t('event.payments.allSettled.message')}
                </p>
            </div>
        );
    }

    return (
        <div className="glass-card overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between">
                    <div className="flex items-center">
                        <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center mr-3">
                            <FontAwesomeIcon icon={faHandHoldingUsd} className="text-purple-600 dark:text-purple-400"/>
                        </div>
                        <div>
                            <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                                {t('event.payments.title')}
                            </h4>
                        </div>
                    </div>

                    <div className="flex items-center space-x-3">
                        {/* Export Button */}
                        <button
                            onClick={handleExportSettlements}
                            className="flex items-center px-3 py-1.5 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                            title={t('event.export.exportSettlements')}
                            aria-label={t('event.export.exportSettlements')}
                        >
                            <FontAwesomeIcon icon={faDownload} className="mr-2" aria-hidden="true"/>
                            <span className="hidden sm:inline">{t('event.export.exportSettlements')}</span>
                            <span className="sm:hidden" aria-hidden="true">CSV</span>
                        </button>

                        {/* Sponsor Surplus Distribution Toggle */}
                        {hasSponsorParty && sponsorCoverageInfo && sponsorCoverageInfo.surplus > 0 && (
                            <div className="flex items-center space-x-2">
                                <div className="text-right">
                                    <p className="text-xs font-medium text-gray-900 dark:text-white">
                                        {t('event.payments.sponsor.toggle.title')}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        ({formatCurrency(sponsorCoverageInfo.surplus)})
                                    </p>
                                </div>
                                <button
                                    onClick={() => saveConfig({ distributeSponsorSurplus: !distributeSponsorSurplus })}
                                    disabled={isPlanLocked}
                                    className={`text-2xl text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 rounded-lg ${
                                        isPlanLocked ? 'opacity-60 cursor-not-allowed' : ''
                                    }`}
                                    title={distributeSponsorSurplus ?
                                        t('event.payments.sponsor.toggle.returnToFund') :
                                        t('event.payments.sponsor.toggle.distributeToMembers')
                                    }
                                    aria-label={distributeSponsorSurplus ?
                                        t('event.payments.sponsor.toggle.returnToFund') :
                                        t('event.payments.sponsor.toggle.distributeToMembers')
                                    }
                                    aria-pressed={distributeSponsorSurplus}
                                >
                                    <FontAwesomeIcon icon={distributeSponsorSurplus ? faToggleOn : faToggleOff} />
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Info message */}
                {hasSponsorParty && sponsorCoverageInfo && (
                    <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                        <div className="flex items-start space-x-2">
                            <FontAwesomeIcon
                                icon={sponsorCoverageInfo.shortfall > 0 ? faExclamationTriangle : faInfoCircle}
                                className={`text-sm mt-0.5 ${
                                    sponsorCoverageInfo.shortfall > 0
                                        ? 'text-orange-600 dark:text-orange-400'
                                        : 'text-blue-600 dark:text-blue-400'
                                }`}
                            />
                            <div className="text-xs text-blue-800 dark:text-blue-200">
                                {sponsorCoverageInfo.sponsorCoversAll ? (
                                    distributeSponsorSurplus ? (
                                        <span>{t('event.payments.sponsor.messages.coversAllWithDistribution')}</span>
                                    ) : (
                                        <span>{t('event.payments.sponsor.messages.coversAllReturnFund', { amount: formatCurrency(sponsorCoverageInfo.surplus) })}</span>
                                    )
                                ) : (
                                    <span>
                                        {t('event.payments.sponsor.messages.partialCoverage', {
                                            coverage: sponsorCoverageInfo.coveragePercentage.toFixed(1),
                                            shortfall: formatCurrency(sponsorCoverageInfo.shortfall)
                                        })}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Only shows when the person viewing is owed money */}
            {selectedParticipant && participants[selectedParticipant] && (
                <ReceiverSummary
                    participantId={selectedParticipant}
                    participantName={participants[selectedParticipant]}
                    participantIds={participantIds}
                    expenses={expenses}
                    settlements={rawSettlements}
                />
            )}

            {/* Strategy Selector */}
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                {isPlanLocked ? (
                    renderLockedStrategy()
                ) : (
                    <>
                        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                            {t('event.payments.strategy.label')}
                        </div>
                        {renderStrategySelector()}
                    </>
                )}
            </div>

            {/* Sorting — six ways to order two rows is noise, not a feature. */}
            {sortedSettlements.length >= MIN_SETTLEMENTS_FOR_SORTING && (
                <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                    {renderSortBar()}
                </div>
            )}

            {/* Everything is paid but the auto-close never landed */}
            {isOwnerActing && isSettledButOpen && (
                <div
                    className="px-4 py-2 border-b border-gray-100 dark:border-gray-700 bg-amber-50 dark:bg-amber-900/20"
                    title={t('event.payments.autoClose.stuckMessage')}
                >
                    <div className="flex items-center gap-2">
                        <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            className="flex-shrink-0 text-amber-600 dark:text-amber-400"
                            aria-hidden="true"
                        />
                        <p className="flex-1 min-w-0 text-xs text-amber-800 dark:text-amber-200">
                            {t('event.payments.autoClose.stuckTitle')}
                        </p>
                        <button
                            type="button"
                            onClick={handleCloseStuckEvent}
                            disabled={closingStuckEvent}
                            className="inline-flex items-center gap-1.5 flex-shrink-0 px-2.5 py-1 text-xs font-medium rounded-lg bg-amber-600 hover:bg-amber-700 text-white transition-colors disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-amber-500"
                        >
                            <FontAwesomeIcon
                                icon={closingStuckEvent ? faSpinner : faLock}
                                className={closingStuckEvent ? 'animate-spin' : ''}
                                aria-hidden="true"
                            />
                            {t('event.payments.autoClose.stuckAction')}
                        </button>
                    </div>
                </div>
            )}

            {/* Settlements */}
            <div className="p-4">
                {renderSettlementsList()}
            </div>
        </div>
    );
};

export default SuggestedPaymentsTab;
